import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Building2, Eye, Clock, CheckCircle, X,
  Users, Phone, Globe, MapPin, BarChart3,
  AlertTriangle, Settings, LogOut, Loader2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../utils/api';

const FacilityDashboardPage = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [facility, setFacility] = useState(null);
  const [analytics, setAnalytics] = useState({ views: 0, inquiries: 0, rating: 0 });

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const data = await apiCall('/api/facility/dashboard', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubmission(data.submission);
        setFacility(data.facility);
        setAnalytics(data.analytics || { views: 0, inquiries: 0, rating: 0 });
      } catch (err) {
        // 404 means this account registered but has no submission on file yet
        setError(err.message?.includes('404') ? 'no-submission' : 'load-failed');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Under Review' },
      approved: { color: 'bg-green-100 text-green-800', text: 'Active' },
      rejected: { color: 'bg-red-100 text-red-800', text: 'Rejected' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={`${config.color} border-0`}>{config.text}</Badge>;
  };

  const DashboardHeader = () => (
    <div className="bg-white shadow-sm border-b">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex items-center text-gray-600 hover:text-gray-900">
              <img src="/logo.gif" alt="Addiction Rehab Call Logo" className="h-8 w-auto mr-2" />
              <span className="font-medium">Back to Directory</span>
            </Link>
            <div className="text-gray-300">|</div>
            <h1 className="text-xl font-semibold text-gray-900">Facility Dashboard</h1>
          </div>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <DashboardHeader />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  if (error === 'no-submission') {
    return (
      <div className="min-h-screen bg-gray-50">
        <DashboardHeader />
        <div className="container mx-auto px-4 py-16 text-center">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No facility submission found</h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            This account isn't linked to a facility listing yet.
          </p>
          <Button asChild>
            <Link to="/facility-signup">List Your Facility</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (error === 'load-failed') {
    return (
      <div className="min-h-screen bg-gray-50">
        <DashboardHeader />
        <div className="container mx-auto px-4 py-16 text-center">
          <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Couldn't load your dashboard</h2>
          <p className="text-gray-600">Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  const displayName = facility?.name || submission.facility_name;

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Welcome & Status */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Welcome back, {displayName}
                </h2>
                <p className="text-gray-600">
                  Manage your facility listing and track performance
                </p>
              </div>
              <div className="flex items-center space-x-3">
                {getStatusBadge(submission.status)}
                {facility && (
                  <Button asChild>
                    <Link to={`/facility/${facility.id}`}>
                      <Eye className="h-4 w-4 mr-2" />
                      View Public Listing
                    </Link>
                  </Button>
                )}
              </div>
            </div>

            {submission.status === 'pending' && (
              <Alert className="mb-6">
                <Clock className="h-4 w-4" />
                <AlertDescription>
                  Your facility listing is currently under review. We'll notify you via email once it's approved,
                  typically within 2-3 business days.
                </AlertDescription>
              </Alert>
            )}

            {submission.status === 'rejected' && (
              <Alert className="mb-6 border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-red-700">
                  Your facility listing was not approved.
                  {submission.review_notes && <> Feedback: {submission.review_notes}</>}
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Stats Cards - only meaningful once approved and live */}
          {facility && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Views</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.views.toLocaleString()}</p>
                    </div>
                    <Eye className="h-8 w-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Inquiries</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.inquiries}</p>
                    </div>
                    <Users className="h-8 w-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Status</p>
                      <p className="text-2xl font-bold text-green-600">Active</p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Facility Information */}
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Facility Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-gray-900">Contact Information</h4>
                      <div className="mt-2 space-y-1 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 mr-2" />
                          {submission.phone}
                        </div>
                        {submission.website && (
                          <div className="flex items-center">
                            <Globe className="h-4 w-4 mr-2" />
                            <a href={submission.website} target="_blank" rel="noopener noreferrer"
                               className="text-blue-600 hover:underline">
                              Website
                            </a>
                          </div>
                        )}
                        <div className="flex items-start">
                          <MapPin className="h-4 w-4 mr-2 mt-0.5" />
                          <span>{submission.address}, {submission.city}, {submission.state} {submission.zip_code}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900">Services Offered</h4>
                      <div className="mt-2">
                        {submission.treatment_types?.length > 0 && (
                          <div className="mb-2">
                            <span className="text-sm font-medium text-gray-700">Treatment Types:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {submission.treatment_types.map((type) => (
                                <Badge key={type} variant="secondary" className="text-xs">{type}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {submission.specializations?.length > 0 && (
                          <div>
                            <span className="text-sm font-medium text-gray-700">Specializations:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {submission.specializations.map((spec) => (
                                <Badge key={spec} variant="outline" className="text-xs">{spec}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                    <p className="text-sm text-gray-600">{submission.description}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div>
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {facility && (
                    <Button asChild className="w-full" variant="outline">
                      <Link to={`/facility/${facility.id}`}>
                        <Eye className="h-4 w-4 mr-2" />
                        Preview Public Listing
                      </Link>
                    </Button>
                  )}
                  <p className="text-xs text-gray-500 text-center pt-2">
                    Listing edits and detailed analytics are coming soon. To update your information now,
                    contact us directly.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilityDashboardPage;
